<template>
    <div
        v-if="chartVisibility"
        class="bg-transparent rounded-lg h-full"
        ref="queue-activity-wrapper"
        :class="{ 'flex items-center justify-center h-full': !chartData || !chartData.series || !chartData.series.length }"
    >
        <template v-if="chartData && chartData.series && chartData.series.length">
            <div class="queue-activity-container h-full">
                <highcharts
                    ref="queue-activity"
                    class="chart-content_wrapper"
                    :options="chartData"
                    :callback="onInitChartCallback"
                    :updateArgs="[true, true]"
                />
            </div>
        </template>
        <template v-else>
            <div>
                <WidgetNoData :widget="data" />
                <h3 class="text-main-xl text-center">{{ $t('general.noData') }}</h3>
            </div>
        </template>
    </div>
</template>
<script>
    import get from 'lodash/get'
    import { Chart } from 'highcharts-vue'
    import bus from '@/event-bus/EventBus'
    import Highcharts from 'highcharts'
    import { convertHex } from '@/helpers/convertHex'
    import actionMixin from '@/components/Charts/Configs/actionMixin'
    import { queueActivities } from '@/enum/queueDashboardStatistics'
    import activityChartConfig from '@/components/Charts/Configs/ActivityGauge'
    import highchartsMoreInit from 'highcharts/highcharts-more'
    import solidGaugeInit from 'highcharts/modules/solid-gauge'

    highchartsMoreInit(Highcharts)
    solidGaugeInit(Highcharts)

    export default {
        mixins: [actionMixin],
        components: {
            Highcharts,
            highcharts: Chart,
            WidgetNoData: () => import('@/components/Widgets/WidgetNoData')
        },
        props: {
            data: {
                type: Object,
                default: () => ({}),
            },
            queueStatistics: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            statisticCounts() {
                return this.queueStatistics
            },
            activitiesToDisplay() {
                return get(this.data.WidgetLayout, 'ShowActivities', [])
            },
        },
        data() {
            return {
                chartInstance: false,
                chartData: {},
                chartVisibility: true,
                DEFAULT_STYLES: {
                    AnswerCount: {
                        color: '#5EB300',
                        fontSize: 24,
                    },
                    InSLACount: {
                        color: '#61B5FF',
                        fontSize: 24, // 24px
                    },
                },
                wrapperWidth: '100%'
            }
        },
        methods: {
            get,
            onInitChartCallback(chart) {
                this.chartInstance = chart
            },
            tryPrintChart() {
                const divToPrint = this.$el.children[0]
                const newWin = window.open();
                newWin.document.write(divToPrint.innerHTML);
                newWin.document.close();
                newWin.focus();
                newWin.print();
            },
            tryDownloadChart(type) {
                this.$refs['queue-activity'].chart.exportChart({
                    type: type,
                })
            },
            chartOptions() {
                let data = []

                let pane = {
                    startAngle: 0,
                    endAngle: 360,
                    background: [],
                    size: '100%'
                }

                try {
                    let activitiesToDisplay = this.activitiesToDisplay
                    let reversedCounts = false

                    const mainActivity = this.data.WidgetLayout.mainActivity

                    if (mainActivity !== 'AnswerCount') {
                        //Counts: AnswerCount, InSLACount
                        reversedCounts = true
                    }

                    let CallCount = get(this.statisticCounts, 'CallCount')
                    let answerPercentage = 0
                    let inSLAPercentage = 0

                    let AnswerCountStyles = this.data.WidgetLayout['AnswerCount']
                    let InSLACountStyles = this.data.WidgetLayout['InSLACount']
                    let InSLACount = 0
                    activitiesToDisplay.forEach((el) => {

                        if (el === 'AnswerCount') {
                            let AnswerCount = get(this.statisticCounts['percentage'], '[1].value')

                            if (CallCount) {
                                answerPercentage = AnswerCount ? (AnswerCount / CallCount * 100).toFixed(2) : 0
                            }

                            if (activitiesToDisplay.length !== queueActivities.length) {
                                reversedCounts = false
                            }

                            let radius = reversedCounts ? '76%' : '99%'
                            let innerRadius = reversedCounts ? '54%' : '77%'

                            let answerData = {
                                name: this.$t('queue.answer'),
                                data: [{
                                    color: AnswerCountStyles['color'],
                                    radius: radius,
                                    innerRadius: innerRadius,
                                    y: Number(answerPercentage),
                                }],
                            }
                            let rgb = convertHex(AnswerCountStyles['color'])
                            let answerPane = {
                                outerRadius: radius,
                                innerRadius: innerRadius,
                                backgroundColor: `rgba(${rgb},0.3)`,
                                borderWidth: 0,
                            }
                            data.push(answerData)
                            pane['background'].push(answerPane)
                        }
                        if (el === 'InSLACount') {
                            InSLACount = get(this.statisticCounts['primary'], 'InSLACount.value')

                            if (CallCount) {
                                inSLAPercentage = InSLACount ? (InSLACount / CallCount * 100).toFixed(2) : 0
                            }

                            if (activitiesToDisplay.length !== queueActivities.length) {
                                reversedCounts = true
                            }

                            let radius = reversedCounts ? '99%' : '76%'
                            let innerRadius = reversedCounts ? '77%' : '54%'

                            let InSLAData = {
                                name: this.$t('queue.inSLA'),
                                data: [{
                                    color: InSLACountStyles['color'],
                                    radius: radius,
                                    innerRadius: innerRadius,
                                    y: Number(inSLAPercentage),
                                }],
                            }

                            let rgb = convertHex(InSLACountStyles['color'])
                            let InSLAPane = {
                                outerRadius: radius,
                                innerRadius: innerRadius,
                                backgroundColor: `rgba(${rgb},0.3)`,
                                borderWidth: 0,
                            }
                            data.push(InSLAData)
                            pane['background'].push(InSLAPane)
                        }
                    })

                    let dataLabels = ['', '']
                    let yMargin = 0

                    if (activitiesToDisplay.includes('AnswerCount')) {
                        let index = reversedCounts ? 1 : 0
                        dataLabels[index] = `<span style="font-size: ${AnswerCountStyles['fontSize'] + 'px'}; color: ${AnswerCountStyles['color']}">${answerPercentage}%<br>${this.$t('queue.answer')}</span>`
                        yMargin = AnswerCountStyles['fontSize']
                    }

                    if (activitiesToDisplay.includes('InSLACount')) {
                        let index = reversedCounts ? 0 : 1
                        dataLabels[index] = `<span style="font-size: ${InSLACountStyles['fontSize'] + 'px'}; color: ${InSLACountStyles['color']}">${inSLAPercentage}% <label style="font-size: ${InSLACountStyles['fontSize'] - 4}px;">(${InSLACount})</label><br>${this.$t('queue.inSLA')}</span>`
                        yMargin += InSLACountStyles['fontSize']
                    }

                    dataLabels = `<p style="text-align: center;"> ${dataLabels[0]} <br> ${dataLabels[1]}<p>`
                    yMargin = (yMargin * 1.5 - activitiesToDisplay.length * 5)

                    this.chartData = {
                        ...activityChartConfig,
                        series: data,
                        pane,
                        plotOptions: {
                            solidgauge: {
                                dataLabels: {
                                    enabled: true,
                                    format: dataLabels,
                                    y: -yMargin,
                                    borderWidth: 0,
                                    useHTML: true,
                                    zIndex: 100,
                                },
                                linecap: 'round',
                                stickyTracking: false,
                                rounded: true,
                            },
                        },
                    }
                } catch (e) {
                    console.log(e)
                } finally {
                    this.reDrawChart()
                }
            },
            initWidgetConfig() {
                if (!this.data.WidgetLayout.ShowActivities) {
                    this.$set(this.data.WidgetLayout, 'ShowActivities', queueActivities)
                }
                if (!this.data.WidgetLayout['AnswerCount']) {
                    for (let key in this.DEFAULT_STYLES) {
                        this.$set(this.data.WidgetLayout, key, this.DEFAULT_STYLES[key])
                    }
                }
                if (!this.data.WidgetLayout['mainActivity']) {
                    this.$set(this.data.WidgetLayout, 'mainActivity', 'AnswerCount')
                }
            },
            calculateChartContainerParams () {
                const queueActivityWrapper = this.$refs['queue-activity-wrapper']
                if (queueActivityWrapper) {
                    const wrapperRect = queueActivityWrapper.getBoundingClientRect()
                    this.wrapperWidth = wrapperRect.width + 'px'
                }
            },
            onWidgetResizeHandler (widgetID) {
                if (this.data.WidgetID.toString() !== widgetID.toString()) {
                    return
                }
                this.reDrawChart()
            },
            triggerResizeEvent() {
                bus.$on('widget-resized', this.onWidgetResizeHandler)
            },
            reDrawChart() {
                this.calculateChartContainerParams()
                this.chartVisibility = false
                this.$nextTick(() => {
                    this.chartVisibility = true
                })
            },
        },
        watch: {
            statisticCounts: {
                deep: true,
                immediate: true,
                handler() {
                    this.chartOptions()
                },
            },
            data: {
                deep: true,
                handler() {
                    this.chartOptions()
                },
            },
        },
        mounted() {
            this.initWidgetConfig()
            this.triggerResizeEvent()
            this.chartOptions()
        },
        beforeDestroy() {
            bus.$off('widget-resized', this.onWidgetResizeHandler)
        }
    }
</script>
<style lang="scss" scoped>
.chart-wrapper {

}
.chart-content_wrapper {
    height: 100%;
}
</style>
